import { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import { Device } from "@twilio/voice-sdk";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Phone,
  PhoneOff,
  Minimize2,
  Delete,
  Mic,
  MicOff,
  ClipboardPaste,
  Volume2,
  VolumeX,
  Smartphone
} from "lucide-react";

// The Voice SDK constructor registers its teardown twice — once on 'unload'
// and once on 'pagehide' (device.js: addEventListener('unload', destroy) then
// the same for 'pagehide'). Chrome's permissions policy disallows 'unload' in
// this document, so that first registration is refused and logged as
//   [Violation] Permissions policy violation: unload is not allowed
// on every mount, pointing at our `new Device(...)` line. Hide 'unload' from
// the constructor so it never tries: 'pagehide' runs the identical teardown,
// and it fires in the cases 'unload' misses anyway (bfcache, a backgrounded
// phone), so the device is still destroyed on the way out. destroy() later
// removes an 'unload' listener that was never added, which is a no-op.
// The constructor is synchronous, so the patch is restored before anything
// else can observe it.
function newDeviceWithoutUnloadHook(token, options) {
  const addEventListener = window.addEventListener;
  window.addEventListener = function (type, ...rest) {
    if (type === "unload") return;
    return addEventListener.call(this, type, ...rest);
  };
  try {
    return new Device(token, options);
  } finally {
    window.addEventListener = addEventListener;
  }
}

const SipPhone = forwardRef(function SipPhone({ initialNumber = "", onCallStateChange, onStatusChange, onCallMyPhone, hasPhoneNumber }, ref) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(initialNumber);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [callState, setCallState] = useState("idle"); // idle, calling, connected
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeaker, setIsSpeaker] = useState(false);
  const [error, setError] = useState(null);

  const [chaverimMode, setChaverimMode] = useState(() => {
    try { return sessionStorage.getItem('dialer.chaverim_mode') === '1'; } catch { return false; }
  });
  const deviceRef = useRef(null);
  const sessionRef = useRef(null);
  const callTimerRef = useRef(null);
  const tokenExpiryRef = useRef(null);
  const deleteHoldRef = useRef(null);
  const zeroHoldRef = useRef(null);
  const audioObserverRef = useRef(null);
  const speakerStateRef = useRef(false); // mirrors isSpeaker for observer callback

  // Expose expand method to parent via ref
  useImperativeHandle(ref, () => ({
    expand: () => setIsExpanded(true),
  }), []);

  // Notify parent of call state changes
  useEffect(() => {
    onCallStateChange?.(callState);
  }, [callState, onCallStateChange]);

  // Notify parent of registration status changes
  useEffect(() => {
    onStatusChange?.({ isRegistered, isConnecting });
  }, [isRegistered, isConnecting, onStatusChange]);

  // Initialize Twilio Device on mount
  useEffect(() => {
    fetchTokenAndInit();

    return () => {
      cleanup();
    };
  }, []);

  // Update phone number when initialNumber changes
  useEffect(() => {
    if (initialNumber) {
      setPhoneNumber(initialNumber);
      setIsExpanded(true);
    }
  }, [initialNumber]);

  const cleanup = useCallback(() => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
    }
    if (tokenExpiryRef.current) {
      clearTimeout(tokenExpiryRef.current);
    }
    if (audioObserverRef.current) {
      audioObserverRef.current.disconnect();
    }
    if (sessionRef.current) {
      try { sessionRef.current.disconnect(); } catch (e) {}
    }
    if (deviceRef.current) {
      try { deviceRef.current.destroy(); } catch (e) {}
    }
  }, []);

  const fetchTokenAndInit = useCallback(async () => {
    setIsConnecting(true);
    setError(null);

    try {
      const resp = await axios.get(`${API}/voice/token`, { withCredentials: true });
      const token = resp.data.token;

      // Destroy old device if exists
      if (deviceRef.current) {
        try { deviceRef.current.destroy(); } catch (e) {}
      }

      const device = newDeviceWithoutUnloadHook(token, {
        // Twilio Voice SDK uses loglevel levels: trace=0, debug=1, info=2,
        // warn=3, error=4, silent=5. The previous `1` was actually debug
        // and was spamming "[WSTransport] heartbeat / register" lines on
        // every keepalive tick. Bump to error so real failures still
        // surface but the keepalive chatter stays out of DevTools.
        logLevel: "error",
      });

      deviceRef.current = device;

      device.on("registered", () => {
        setIsRegistered(true);
        setIsConnecting(false);
        console.log("Twilio Device registered");
      });

      device.on("unregistered", () => {
        setIsRegistered(false);
      });

      device.on("error", (err) => {
        console.error("Twilio Device error:", err);
        setError(err.message || "Connection error");
        setIsConnecting(false);
      });

      device.on("tokenWillExpire", () => {
        refreshToken();
      });

      await device.register();

      // Schedule token refresh at 50 minutes (TTL is 60 min)
      if (tokenExpiryRef.current) clearTimeout(tokenExpiryRef.current);
      tokenExpiryRef.current = setTimeout(() => refreshToken(), 50 * 60 * 1000);

    } catch (err) {
      console.error("Failed to initialize Twilio Device:", err);
      setError("Failed to connect");
      setIsConnecting(false);
    }
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/voice/token`, { withCredentials: true });
      if (deviceRef.current) {
        deviceRef.current.updateToken(resp.data.token);
        console.log("Twilio token refreshed");
      }
      if (tokenExpiryRef.current) clearTimeout(tokenExpiryRef.current);
      tokenExpiryRef.current = setTimeout(() => refreshToken(), 50 * 60 * 1000);
    } catch (err) {
      console.error("Token refresh failed:", err);
    }
  }, []);

  const makeCall = useCallback(async () => {
    if (!deviceRef.current || !isRegistered || !phoneNumber) return;

    const cleanNumber = phoneNumber.replace(/\D/g, "");
    if (cleanNumber.length < 10) {
      setError("Invalid phone number");
      return;
    }

    setCallState("calling");
    setError(null);

    try {
      const callParams = { To: cleanNumber };
      if (chaverimMode) {
        callParams.caller_id_override = "+17182221800";
      }
      const call = await deviceRef.current.connect({
        params: callParams
      });

      sessionRef.current = call;

      call.on("accept", () => {
        setCallState("connected");
        startCallTimer();
      });

      call.on("disconnect", () => {
        endCall();
      });

      call.on("cancel", () => {
        endCall();
      });

      call.on("error", (err) => {
        console.error("Call error:", err);
        setError("Call failed");
        endCall();
      });

    } catch (err) {
      console.error("Call failed:", err);
      setError("Call failed");
      setCallState("idle");
    }
  }, [isRegistered, phoneNumber, chaverimMode]);

  const toggleChaverimMode = useCallback(() => {
    setChaverimMode((prev) => {
      const next = !prev;
      try { sessionStorage.setItem('dialer.chaverim_mode', next ? '1' : '0'); } catch {}
      if (navigator.vibrate) navigator.vibrate([50, 40, 50]);
      return next;
    });
  }, []);

  const startZeroHold = useCallback(() => {
    if (zeroHoldRef.current) clearTimeout(zeroHoldRef.current);
    zeroHoldRef.current = setTimeout(() => {
      toggleChaverimMode();
      zeroHoldRef.current = "fired"; // prevent the trailing tap from inserting "0"
    }, 2000);
  }, [toggleChaverimMode]);

  const endZeroHold = useCallback((allowTap = true) => {
    const v = zeroHoldRef.current;
    if (v === "fired") {
      zeroHoldRef.current = null;
      return false; // long-press consumed — suppress the tap
    }
    if (v) clearTimeout(v);
    zeroHoldRef.current = null;
    return allowTap;
  }, []);

  const hangup = useCallback(() => {
    if (sessionRef.current) {
      try { sessionRef.current.disconnect(); } catch (e) {}
    }
    endCall();
  }, []);

  const endCall = useCallback(() => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    sessionRef.current = null;
    setCallState("idle");
    setCallDuration(0);
    setIsMuted(false);
    setIsSpeaker(false);
  }, []);

  const startCallTimer = useCallback(() => {
    setCallDuration(0);
    callTimerRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  }, []);

  const toggleMute = useCallback(() => {
    if (!sessionRef.current) return;
    const newMuted = !isMuted;
    sessionRef.current.mute(newMuted);
    setIsMuted(newMuted);
  }, [isMuted]);

  // ---- Audio output routing (speaker/earpiece) ----

  // Find the best earpiece device ID from available outputs
  const findEarpieceId = useCallback(async () => {
    // 1) Try Twilio SDK's available output devices
    const twDev = deviceRef.current;
    if (twDev?.audio?.availableOutputDevices?.size) {
      const available = twDev.audio.availableOutputDevices;
      // Look for "communications" device
      if (available.has("communications")) return "communications";
      // Look for earpiece by label
      for (const [id, info] of available) {
        if (info.label?.toLowerCase().includes("earpiece")) return id;
      }
    }
    // 2) Try browser enumerateDevices
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const outputs = devices.filter(d => d.kind === "audiooutput");
      for (const d of outputs) {
        if (d.deviceId === "communications") return "communications";
        if (d.label?.toLowerCase().includes("earpiece")) return d.deviceId;
      }
    } catch {}
    // 3) Last resort: "communications" string (works on some Android Chrome)
    return "communications";
  }, []);

  // Apply sinkId to a single audio element
  const applySinkToElement = useCallback(async (el, speakerOn) => {
    if (typeof el.setSinkId !== "function") return;
    try {
      if (speakerOn) {
        await el.setSinkId(""); // default = speaker
      } else {
        const earpieceId = await findEarpieceId();
        await el.setSinkId(earpieceId);
      }
    } catch (e) {
      console.warn("setSinkId failed:", e.message);
    }
  }, [findEarpieceId]);

  // Apply audio output to all current audio elements + via Twilio SDK
  const applyAudioOutput = useCallback((speakerOn) => {
    // Method 1: Twilio SDK audio device management
    const twDev = deviceRef.current;
    if (twDev?.audio?.speakerDevices) {
      try {
        if (speakerOn) {
          twDev.audio.speakerDevices.set("default");
        } else {
          findEarpieceId().then(id => {
            try { twDev.audio.speakerDevices.set(id); } catch {}
          });
        }
      } catch (e) {
        console.warn("Twilio audio device set failed:", e.message);
      }
    }

    // Method 2: Direct setSinkId on all audio elements
    document.querySelectorAll("audio").forEach(el => applySinkToElement(el, speakerOn));
  }, [findEarpieceId, applySinkToElement]);

  // MutationObserver: catch audio elements Twilio creates dynamically
  useEffect(() => {
    if (callState === "idle") {
      // Clean up observer when not on a call
      if (audioObserverRef.current) {
        audioObserverRef.current.disconnect();
        audioObserverRef.current = null;
      }
      return;
    }

    // Start observing for new audio elements
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeName === "AUDIO") {
            applySinkToElement(node, speakerStateRef.current);
          }
          // Also check children (Twilio may add audio inside a wrapper)
          if (node.querySelectorAll) {
            node.querySelectorAll("audio").forEach(el => {
              applySinkToElement(el, speakerStateRef.current);
            });
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    audioObserverRef.current = observer;

    return () => {
      observer.disconnect();
      audioObserverRef.current = null;
    };
  }, [callState, applySinkToElement]);

  const toggleSpeaker = useCallback(() => {
    const newSpeaker = !isSpeaker;
    setIsSpeaker(newSpeaker);
    speakerStateRef.current = newSpeaker;
    applyAudioOutput(newSpeaker);
    if (navigator.vibrate) navigator.vibrate(30);
  }, [isSpeaker, applyAudioOutput]);

  // When a call connects, default to earpiece (not speaker)
  useEffect(() => {
    if (callState === "connected") {
      speakerStateRef.current = false;
      setIsSpeaker(false);
      // Apply with multiple delays to catch late-created audio elements
      const t1 = setTimeout(() => applyAudioOutput(false), 300);
      const t2 = setTimeout(() => applyAudioOutput(false), 800);
      const t3 = setTimeout(() => applyAudioOutput(false), 1500);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }
  }, [callState, applyAudioOutput]);

  const handleDialpadPress = useCallback((digit) => {
    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(30);
    }

    setPhoneNumber((prev) => prev + digit);

    // Send DTMF if in call
    if (sessionRef.current && callState === "connected") {
      try {
        sessionRef.current.sendDigits(digit);
      } catch (e) {
        console.error("DTMF error:", e);
      }
    }
  }, [callState]);

  const handleBackspace = useCallback(() => {
    setPhoneNumber((prev) => prev.slice(0, -1));
  }, []);

  // Desktop keyboard: while the dialer is visible, type digits on the keyboard
  // and they flow into the phone number. Enter dials. Backspace deletes.
  // Holding "0" for 2s toggles Chaverim mode (matches the on-screen long press).
  useEffect(() => {
    const dialerVisible = isExpanded || callState !== "idle";
    if (!dialerVisible) return;

    const isTypingInInput = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (el.isContentEditable) return true;
      return false;
    };

    let keyboardZeroHold = null;
    let zeroToggledByHold = false;

    const onKeyDown = (e) => {
      if (isTypingInInput()) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const k = e.key;
      // Digit / dtmf input
      if (/^[0-9*#]$/.test(k)) {
        if (k === "0") {
          // Start long-press timer on first keydown only (ignore OS auto-repeats).
          if (!e.repeat && keyboardZeroHold === null && !zeroToggledByHold) {
            keyboardZeroHold = setTimeout(() => {
              toggleChaverimMode();
              zeroToggledByHold = true;
              keyboardZeroHold = null;
            }, 2000);
          }
          // Swallow auto-repeat of "0" so we don't spam digits while holding.
          if (e.repeat) {
            e.preventDefault();
            return;
          }
        }
        e.preventDefault();
        handleDialpadPress(k);
        return;
      }
      if (k === "Backspace") {
        e.preventDefault();
        handleBackspace();
        return;
      }
      if (k === "Enter") {
        e.preventDefault();
        if (callState === "idle") makeCall();
        return;
      }
    };

    const onKeyUp = (e) => {
      if (e.key === "0") {
        if (keyboardZeroHold) {
          clearTimeout(keyboardZeroHold);
          keyboardZeroHold = null;
        }
        if (zeroToggledByHold) {
          // The keydown already inserted a "0"; undo that since the hold toggled the mode.
          setPhoneNumber((prev) => (prev.endsWith("0") ? prev.slice(0, -1) : prev));
          zeroToggledByHold = false;
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      if (keyboardZeroHold) clearTimeout(keyboardZeroHold);
    };
  }, [isExpanded, callState, handleDialpadPress, handleBackspace, makeCall, toggleChaverimMode]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatPhoneDisplay = (number) => {
    const clean = number.replace(/\D/g, "");
    if (clean.length <= 3) return clean;
    if (clean.length <= 6) return `(${clean.slice(0, 3)}) ${clean.slice(3)}`;
    return `(${clean.slice(0, 3)}) ${clean.slice(3, 6)}-${clean.slice(6, 10)}`;
  };

  const dialpadButtons = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["*", "0", "#"]
  ];

  // When idle and not expanded, render nothing (sidebar button handles idle state)
  if (callState === "idle" && !isExpanded) {
    return null;
  }

  // Minimized during a call — show floating pill
  if (!isExpanded && callState !== "idle") {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-full shadow-2xl transition-colors active:scale-95"
      >
        <Phone className="w-4 h-4 animate-pulse" />
        <span className="text-sm font-medium">
          {callState === "connected" ? formatDuration(callDuration) : "Calling..."}
        </span>
      </button>
    );
  }

  // Expanded phone widget
  return (
    <div className="fixed bottom-4 right-4 z-50 w-72 bg-slate-900 border border-slate-700 rounded-lg shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-slate-800 border-b border-slate-700">
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-medium">Dispatch Phone</span>
        </div>
        <div className="flex items-center gap-1">
          <span className={`w-2 h-2 rounded-full ${isRegistered ? "bg-green-500" : isConnecting ? "bg-yellow-500 animate-pulse" : "bg-red-500"}`} />
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setIsExpanded(false)}>
            <Minimize2 className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Chaverim mode banner — toggled by holding "0" for 2 seconds */}
      {chaverimMode && (
        <div className="bg-amber-500 text-black text-center text-xs font-bold py-1 px-2">
          CHAVERIM MODE — Calls show (718) 222-1800
        </div>
      )}

      {/* Display */}
      <div className="p-3 bg-slate-950">
        <div className="text-center mb-2">
          {callState === "connected" && (
            <div className="text-green-400 text-xs mb-1">Connected - {formatDuration(callDuration)}</div>
          )}
          {callState === "calling" && (
            <div className="text-yellow-400 text-xs mb-1 animate-pulse">Calling...</div>
          )}
          {error && (
            <div className="text-red-400 text-xs mb-1">{error}</div>
          )}
        </div>
        <div className="bg-slate-800 rounded px-3 py-2 text-center font-mono text-xl tracking-wider min-h-[2.5rem] flex items-center justify-center">
          {formatPhoneDisplay(phoneNumber) || <span className="text-slate-500">Enter number</span>}
        </div>
      </div>

      {/* Dialpad */}
      <div className="p-2 grid grid-cols-3 gap-1">
        {dialpadButtons.map((row, rowIndex) => (
          row.map((digit) => {
            const isZero = digit === "0";
            return (
              <Button
                key={digit}
                variant="ghost"
                className={`h-12 text-lg font-medium hover:bg-slate-800 ${isZero && chaverimMode ? "ring-1 ring-amber-500" : ""}`}
                onClick={() => {
                  if (isZero) {
                    // suppress the "0" tap if a long-press fired
                    if (!endZeroHold(true)) return;
                  }
                  handleDialpadPress(digit);
                }}
                onTouchStart={isZero ? startZeroHold : undefined}
                onTouchEnd={isZero ? () => endZeroHold(false) : undefined}
                onTouchCancel={isZero ? () => endZeroHold(false) : undefined}
                onMouseDown={isZero ? startZeroHold : undefined}
                onMouseUp={isZero ? () => endZeroHold(false) : undefined}
                onMouseLeave={isZero ? () => endZeroHold(false) : undefined}
                disabled={callState === "calling"}
                title={isZero ? "Hold 2s to toggle Chaverim mode" : undefined}
              >
                {digit}
              </Button>
            );
          })
        ))}
      </div>

      {/* Paste & Backspace */}
      <div className="px-2 pb-2 flex gap-1">
        <Button
          variant="ghost"
          className="flex-1 h-8 text-slate-400 hover:text-white"
          onClick={async () => {
            try {
              const text = await navigator.clipboard.readText();
              if (text) {
                let digits = text.trim().replace(/\D/g, "");
                // Strip leading "1" country code for US numbers
                if (digits.length === 11 && digits[0] === "1") {
                  digits = digits.slice(1);
                }
                if (digits) setPhoneNumber(digits);
              }
            } catch (e) {
              console.error("Clipboard paste failed:", e);
            }
          }}
          disabled={callState !== "idle"}
        >
          <ClipboardPaste className="w-4 h-4 mr-1" />
          <span className="text-xs">Paste</span>
        </Button>
        <Button
          variant="ghost"
          className="flex-1 h-8 text-slate-400 hover:text-white select-none"
          onClick={handleBackspace}
          onTouchStart={() => {
            deleteHoldRef.current = setTimeout(() => {
              setPhoneNumber("");
              if (navigator.vibrate) navigator.vibrate(50);
            }, 1000);
          }}
          onTouchEnd={() => { if (deleteHoldRef.current) clearTimeout(deleteHoldRef.current); }}
          onTouchCancel={() => { if (deleteHoldRef.current) clearTimeout(deleteHoldRef.current); }}
          onMouseDown={() => {
            deleteHoldRef.current = setTimeout(() => {
              setPhoneNumber("");
              if (navigator.vibrate) navigator.vibrate(50);
            }, 1000);
          }}
          onMouseUp={() => { if (deleteHoldRef.current) clearTimeout(deleteHoldRef.current); }}
          onMouseLeave={() => { if (deleteHoldRef.current) clearTimeout(deleteHoldRef.current); }}
          disabled={callState !== "idle" || !phoneNumber}
        >
          <Delete className="w-4 h-4 mr-1" />
          <span className="text-xs select-none">Delete</span>
        </Button>
      </div>

      {/* Call controls */}
      <div className="p-3 border-t border-slate-700 flex gap-2">
        {callState === "idle" ? (
          <>
            <Button
              className="flex-1 bg-green-600 hover:bg-green-700"
              onClick={makeCall}
              disabled={!isRegistered || !phoneNumber}
            >
              <Phone className="w-4 h-4 mr-2" />
              Call
            </Button>
            {hasPhoneNumber && onCallMyPhone && (
              <Button
                className="bg-purple-600 hover:bg-purple-700 px-4"
                onClick={() => {
                  const clean = phoneNumber.replace(/\D/g, "");
                  if (clean.length >= 10) onCallMyPhone(clean);
                }}
                disabled={!phoneNumber || phoneNumber.replace(/\D/g, "").length < 10}
                title="Call My Phone"
              >
                <Smartphone className="w-4 h-4 mr-1" />
                <span className="text-xs">My Phone</span>
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              variant="outline"
              className={`flex-1 ${isMuted ? "bg-red-900/50 border-red-700" : ""}`}
              onClick={toggleMute}
              title={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </Button>
            <Button
              variant="outline"
              className={`flex-1 ${isSpeaker ? "bg-blue-900/50 border-blue-700" : ""}`}
              onClick={toggleSpeaker}
              title={isSpeaker ? "Switch to earpiece" : "Switch to speaker"}
            >
              {isSpeaker ? <Volume2 className="w-4 h-4 text-blue-400" /> : <VolumeX className="w-4 h-4" />}
            </Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700"
              onClick={hangup}
            >
              <PhoneOff className="w-4 h-4 mr-2" />
              End
            </Button>
          </>
        )}
      </div>

      {/* Reconnect button if disconnected */}
      {!isRegistered && !isConnecting && (
        <div className="px-3 pb-3">
          <Button
            variant="outline"
            className="w-full text-xs"
            onClick={fetchTokenAndInit}
          >
            Reconnect
          </Button>
        </div>
      )}
    </div>
  );
});

export default SipPhone;
